import type {
  ResultatsClasseEtudiantEnrichi,
  ResultatsClasseLigneSemestre,
  ResultatsClassePayload,
} from '@/types/resultatsClasse'
import { getMentionFromMoyenne } from '@/utils/noteMoyenneHelpers'
import { enrichResultatsClassePayload } from '@/utils/resultatsClasseHelpers'

export type DecisionJury = 'Admis' | 'Passage conditionnel' | 'Redouble'

export type SessionJuryLabel = 'Normale' | 'Deuxième' | '-'

export interface ResultatsAnnuelsSemestreCol {
  numero: number
  moyenne: number | null
  credits: number
  /** Session de la fiche retenue pour ce semestre (normale ou deuxième). */
  session: SessionJuryLabel
}

export interface ResultatsAnnuelsLigne {
  inscription_id: number
  matricule: string
  prenom: string
  nom: string
  semestres: ResultatsAnnuelsSemestreCol[]
  moyenneAnnuelle: number | null
  creditsTotaux: number
  mention: string
  decision: DecisionJury
  session: SessionJuryLabel
  commentaires: string
}

export interface ResultatsAnnuelsTable {
  title: string
  sheetName: string
  semestreNumeros: number[]
  lignes: ResultatsAnnuelsLigne[]
  effectifs: ResultatsClassePayload['effectifs']
  seuils: ResultatsClassePayload['seuils']
}

export function mentionAnnuelleFromMoyenne(moyenne: number | null | undefined): string {
  const raw = getMentionFromMoyenne(moyenne)
  if (raw === 'insuffisant') return 'Insuffisant'
  return raw
}

export function pickFichesFinalesParSemestre(
  lignes: ResultatsClasseLigneSemestre[] | undefined
): ResultatsClasseLigneSemestre[] {
  const bySemestre = new Map<number, ResultatsClasseLigneSemestre>()
  for (const ligne of lignes ?? []) {
    const existing = bySemestre.get(ligne.semestre_id)
    if (!existing || ligne.type_deliberation === 'seconde') {
      bySemestre.set(ligne.semestre_id, ligne)
    }
  }
  return [...bySemestre.values()].sort((a, b) => a.semestre_numero - b.semestre_numero)
}

export function sessionJuryFromFiches(fiches: ResultatsClasseLigneSemestre[]): SessionJuryLabel {
  if (fiches.length === 0) return '-'
  return fiches.some((fiche) => fiche.type_deliberation === 'seconde') ? 'Deuxième' : 'Normale'
}

/** Session du semestre : la fiche finale est « seconde » si l'étudiant est passé en deuxième session. */
export function sessionSemestreLabel(
  type: ResultatsClasseLigneSemestre['type_deliberation'] | null | undefined
): SessionJuryLabel {
  if (type === 'seconde') return 'Deuxième'
  if (type === 'premiere') return 'Normale'
  return '-'
}

export function resolveSemestreNumeros(payload: ResultatsClassePayload): number[] {
  const fromDisponibilite = (payload.disponibilite?.semestres ?? [])
    .map((s) => s.semestre_numero)
    .filter((n) => Number.isFinite(n))
  if (fromDisponibilite.length > 0) {
    return [...new Set(fromDisponibilite)].sort((a, b) => a - b)
  }

  const fromStudents = [
    ...payload.passage_superieur,
    ...payload.admis_avec_rattrapage,
    ...payload.redouble,
  ].flatMap((row) => (row.credits_par_semestre ?? []).map((l) => l.semestre_numero))
  return [...new Set(fromStudents)].sort((a, b) => a - b)
}

function buildLigne(
  etudiant: ResultatsClasseEtudiantEnrichi,
  semestreNumeros: number[],
  decision: DecisionJury
): ResultatsAnnuelsLigne {
  const fiches = pickFichesFinalesParSemestre(etudiant.credits_par_semestre)
  const byNumero = new Map(fiches.map((fiche) => [fiche.semestre_numero, fiche]))
  const semestres = semestreNumeros.map((numero) => {
    const fiche = byNumero.get(numero)
    return {
      numero,
      moyenne: fiche?.moyenne_generale ?? null,
      credits: fiche?.credits_obtenus ?? 0,
      session: sessionSemestreLabel(fiche?.type_deliberation),
    }
  })

  return {
    inscription_id: etudiant.inscription_id,
    matricule: etudiant.matricule,
    prenom: (etudiant.prenom || '').trim(),
    // Fichier source : noms en majuscules (AIDARA, BA, CISSE…)
    nom: (etudiant.nom || '').trim().toUpperCase(),
    semestres,
    moyenneAnnuelle: etudiant.moyenne,
    creditsTotaux: etudiant.credits_totaux,
    mention: mentionAnnuelleFromMoyenne(etudiant.moyenne),
    decision,
    session: sessionJuryFromFiches(fiches),
    commentaires: '',
  }
}

export function buildResultatsAnnuelsTitle(payload: ResultatsClassePayload): string {
  const annee = (payload.session.annee_universitaire || '').trim().replace(/\//g, '-')
  const session = (payload.session.nom || payload.session.code || '').trim().toUpperCase()
  const parts = ['RESULTATS ANNUELS']
  if (annee) parts.push(annee)
  const head = parts.join(' ')
  return session ? `${head} - ${session}` : head
}

export function buildResultatsAnnuelsSheetName(
  payload: ResultatsClassePayload,
  anonymise: boolean
): string {
  const base = (payload.session.nom || payload.session.code || 'Resultats').trim()
  const raw = anonymise ? `${base} anon` : base
  const cleaned = raw
    .replace(/[\\/?*:[\]]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[-\s]+|[-\s']+$/g, '')
    .trim()
  return (cleaned || 'Resultats').slice(0, 31)
}

export function buildResultatsAnnuelsTable(
  payload: ResultatsClassePayload,
  etudiantsBis: any[],
  options?: { anonymise?: boolean }
): ResultatsAnnuelsTable {
  const enriched = enrichResultatsClassePayload(payload, etudiantsBis)
  const semestreNumeros = resolveSemestreNumeros(payload)
  const lignes: ResultatsAnnuelsLigne[] = [
    ...enriched.passage_superieur.map((row) => buildLigne(row, semestreNumeros, 'Admis')),
    ...enriched.admis_avec_rattrapage.map((row) =>
      buildLigne(row, semestreNumeros, 'Passage conditionnel')
    ),
    ...enriched.redouble.map((row) => buildLigne(row, semestreNumeros, 'Redouble')),
  ]

  lignes.sort((a, b) => {
    if (options?.anonymise) {
      return a.matricule.localeCompare(b.matricule, 'fr', { numeric: true, sensitivity: 'base' })
    }
    const byNom = a.nom.localeCompare(b.nom, 'fr', { numeric: true, sensitivity: 'base' })
    if (byNom !== 0) return byNom
    return a.prenom.localeCompare(b.prenom, 'fr', { numeric: true, sensitivity: 'base' })
  })

  return {
    title: buildResultatsAnnuelsTitle(payload),
    sheetName: buildResultatsAnnuelsSheetName(payload, Boolean(options?.anonymise)),
    semestreNumeros,
    lignes,
    effectifs: payload.effectifs,
    seuils: payload.seuils,
  }
}

export function formatPourcentageFr(part: number, total: number): string {
  if (total <= 0) return '0,00 %'
  const pct = (part / total) * 100
  return `${pct.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`
}

export function buildResultatsAnnuelsFilename(
  payload: ResultatsClassePayload,
  anonymise: boolean
): string {
  const session = payload.session.code || payload.session.nom || 'session'
  const spec = payload.specialite.code || payload.specialite.intitule || 'specialite'
  const safe = (s: string) => s.replace(/[^\w\-]+/g, '_').replace(/_+/g, '_')
  const suffix = anonymise ? '_anonyme' : ''
  return `Resultats_annuels_${safe(session)}_${safe(spec)}${suffix}.xlsx`
}
